/**
 * Notification utilities for PWC
 * Handles audio notifications and system notifications
 */

export class NotificationManager {
  private static instance: NotificationManager;
  private audioContext: AudioContext | null = null;
  private hasUserInteracted = false;

  private constructor() {
    // Listen for first user interaction to enable audio
    this.setupUserInteractionListener();
  }

  static getInstance(): NotificationManager {
    if (!NotificationManager.instance) {
      NotificationManager.instance = new NotificationManager();
    }
    return NotificationManager.instance;
  }

  private setupUserInteractionListener() {
    const enableAudio = () => {
      this.hasUserInteracted = true;
      // Initialize audio context on first interaction
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      // Remove listeners after first interaction
      document.removeEventListener('touchstart', enableAudio);
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };

    document.addEventListener('touchstart', enableAudio, { once: true });
    document.addEventListener('click', enableAudio, { once: true });
    document.addEventListener('keydown', enableAudio, { once: true });
  }

  /**
   * Play the default system notification sound
   * Uses Web Audio API to generate a sound similar to SMS notifications
   */
  async playNotificationSound(): Promise<void> {
    
    if (!this.hasUserInteracted) {
      return;
    }

    try {
      // Try to use the system's default notification sound first
      await this.playSystemNotification();
    } catch (error) {
      // Fallback to generated notification sound
      await this.playFallbackNotification();
    }
  }

  /**
   * Attempt to trigger system notification with sound
   */
  private async playSystemNotification(): Promise<void> {
    // Request notification permission if not already granted
    if ('Notification' in window) {
      let permission = Notification.permission;
      
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission === 'granted') {
        // Create notification with system sound - follows Web API best practices
        const notification = new Notification('New message from your server', {
          body: 'You have a new message',
          icon: '/pwa-192x192.png',
          badge: '/pwa-192x192.png',
          silent: false, // Triggers system notification sound (industry standard)
          tag: 'server-message', // Prevents notification spam (best practice)
          requireInteraction: false,
          // Additional Web API best practices
          timestamp: Date.now(),
          renotify: true, // Allow sound on repeat notifications with same tag
          vibrate: [200, 100, 200] // Haptic feedback on supported devices
        });

        // Auto-close notification after 3 seconds
        setTimeout(() => {
          notification.close();
        }, 3000);

        return;
      }
    }

    throw new Error('Notifications not supported or not permitted');
  }

  /**
   * Fallback notification sound using Web Audio API
   * Generates a pleasant notification tone similar to SMS sounds
   */
  private async playFallbackNotification(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // Create a pleasant two-tone notification sound
    const duration = 0.3;
    const now = this.audioContext.currentTime;

    // First tone (higher pitch)
    const oscillator1 = this.audioContext.createOscillator();
    const gainNode1 = this.audioContext.createGain();
    
    oscillator1.connect(gainNode1);
    gainNode1.connect(this.audioContext.destination);
    
    oscillator1.frequency.setValueAtTime(800, now); // 800Hz
    oscillator1.type = 'sine';
    
    gainNode1.gain.setValueAtTime(0, now);
    gainNode1.gain.linearRampToValueAtTime(0.3, now + 0.01);
    gainNode1.gain.exponentialRampToValueAtTime(0.001, now + duration * 0.5);
    
    oscillator1.start(now);
    oscillator1.stop(now + duration * 0.5);

    // Second tone (lower pitch) - slight delay for SMS-like effect
    setTimeout(() => {
      const oscillator2 = this.audioContext!.createOscillator();
      const gainNode2 = this.audioContext!.createGain();
      
      oscillator2.connect(gainNode2);
      gainNode2.connect(this.audioContext!.destination);
      
      const now2 = this.audioContext!.currentTime;
      oscillator2.frequency.setValueAtTime(600, now2); // 600Hz
      oscillator2.type = 'sine';
      
      gainNode2.gain.setValueAtTime(0, now2);
      gainNode2.gain.linearRampToValueAtTime(0.25, now2 + 0.01);
      gainNode2.gain.exponentialRampToValueAtTime(0.001, now2 + duration * 0.7);
      
      oscillator2.start(now2);
      oscillator2.stop(now2 + duration * 0.7);
    }, 100);
  }

  /**
   * Request notification permissions
   */
  async requestPermissions(): Promise<boolean> {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }

  /**
   * Check if notifications are supported and permitted
   */
  isNotificationSupported(): boolean {
    return 'Notification' in window;
  }

  /**
   * Get current notification permission status
   */
  getNotificationPermission(): NotificationPermission | 'unsupported' {
    if ('Notification' in window) {
      return Notification.permission;
    }
    return 'unsupported';
  }
}

export default NotificationManager.getInstance();
